import { useEffect, useState } from 'react';
import { Button } from '@/ui/Button';
import { Field, Input } from '@/ui/Field';
import {
  tokenDeRestablecer, useEntrar, useMarca, usePedirEnlace, useRestablecerClave,
} from './api';

/**
 * La entrada al portal: entrar, pedir enlace y fijar contraseña nueva.
 *
 * ## Por qué las tres juntas
 *
 * Eran tres `<div class="login-screen hidden">` que `showLogin()`,
 * `showForgot()` y `showReset()` alternaban quitando y poniendo la clase. No son
 * tres destinos: son tres momentos de lo mismo, y moverse entre ellos no es
 * navegar. Aquí es un estado.
 *
 * Las tres claves del puente —`acceso`, `acceso-olvide`, `acceso-restablecer`—
 * existen porque `app.js` sigue decidiendo cuál se ve, y `APP.showLogin()` es
 * contrato: lo llaman `auth.js` al cerrar sesión y `api.js` cuando el servidor
 * responde 401.
 *
 * ## Lo que cambia
 *
 * - **La marca es un dato, no una inyección.** `applyBranding()` la metía
 *   después, con selectores posicionales —`.login-hero-brand div div:first-child`
 *   y `div:last-child`— y reescribía `.logo-mark` con `innerHTML` y un
 *   `style.cssText` de diez propiedades. Cualquier cambio en el orden de esos
 *   `<div>` movía el nombre de la empresa al sitio del portal, sin error.
 * - **El campo de contraseña se puede rellenar con un gestor.** El formulario
 *   llevaba `autocomplete="off"`, que en una intranet corporativa empuja a
 *   contraseñas que uno pueda recordar — es decir, peores. Ahora declara
 *   `email` y `current-password`, que es lo que un gestor necesita.
 * - **El botón de ver la contraseña dice lo que hace.** Era un botón con un
 *   icono y nada más: un lector de pantalla leía «botón».
 * - **El «mínimo 8 caracteres» ya no es solo de aquí.** Lo comprueba también el
 *   servidor desde el 2026-09-28; esto es la respuesta rápida, no la guarda.
 *
 * ## El rediseño del 2026-10-01: separar el acceso de la portada
 *
 * La portada pública (`bienvenida.html`) dice QUÉ HAY DENTRO; esta pantalla
 * solo tiene que decir «estás entrando al Portal de Bienestar, y es de fiar».
 * Repetían lo mismo —Formación, Solicitudes, auditoría— en dos sitios seguidos.
 * Ahora el panel de la izquierda es marca, un titular y una línea de confianza,
 * y el formulario es la única superficie clara de la pantalla.
 *
 * Todo lo de ese cambio es de presentación: los tres `useMutation`, los
 * `onSubmit`, `startApp`, `showForgot`, `showLogin` y los `autoComplete` son
 * los mismos, línea por línea. El movimiento —entrada escalonada, titular que
 * rota— se apaga entero con `prefers-reduced-motion` (`tokens.css` y
 * `Titular`).
 */

type Modo = 'entrar' | 'olvide' | 'restablecer';

type DatosDeMarca = {
  portal_name: string;
  company_name: string;
  login_subtitle: string;
  logo_url: string | null;
};

/** El escalonado de la entrada se pide con una custom property —ver `.anim-sube`
 *  en `tokens.css`—. React no tipa las `--variables` en `style`, de ahí el cast. */
const retardo = (ms: number) => ({ '--retardo': `${ms}ms` }) as React.CSSProperties;

export function Acceso({ modo = 'entrar' }: { modo?: Modo }) {
  const marca = useMarca().data;

  return (
    <div className="flex min-h-screen w-full">
      {modo === 'entrar' ? (
        <div className="grid w-full lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
          <Presentacion marca={marca} />
          <div className="flex flex-col items-center justify-center gap-6 p-4 sm:p-6">
            {/* En móvil el panel de la izquierda no existe, así que la marca
                sube aquí, encima de la tarjeta y sobre el mismo navy. Sin ella la
                pantalla de entrada no diría de quién es. `lg:hidden`, no
                `hidden max-lg:flex`: ver la nota de `Presentacion`. */}
            <div className="anim-sube-corto flex items-center gap-3 text-on-brand lg:hidden">
              <Logo marca={marca} />
              <div className="min-w-0">
                <p className="truncate text-4 font-bold">{marca?.portal_name ?? 'Portal de Bienestar'}</p>
                <p className="truncate text-2 text-white/75">
                  {marca?.company_name ?? 'Grupo Empresarial Su Servicio'}
                </p>
              </div>
            </div>
            <div className="w-full max-w-[400px]">
              <Entrar />
            </div>
          </div>
        </div>
      ) : (
        <div className="flex w-full items-center justify-center p-4 sm:p-6">
          <div className="w-full max-w-[400px]">
            <Tarjeta marca={marca} conMarca>
              {modo === 'olvide' ? <Olvide /> : <Restablecer />}
            </Tarjeta>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── El panel de la izquierda ──────────────────────────────────────────── */

/**
 * Lo que el portal promete, en una línea.
 *
 * Eran tres bloques con icono, título y párrafo —«Formación con certificado»,
 * «Solicitudes en línea», «Cada acción queda registrada»— y la portada pública
 * los repite con más detalle justo antes de llegar aquí. En el acceso la
 * pregunta ya no es «qué hay dentro» sino «¿es este el sitio y es de fiar?»,
 * así que las dos primeras se quedan como etiquetas y la tercera —la única que
 * habla de los datos de quien está a punto de escribir su contraseña— se
 * conserva ENTERA, como nota al pie.
 *
 * Sustituyeron en su día a tres métricas de vanidad —«26 Módulos», «100% Tu
 * equipo», «24/7 Disponible»—: quien mira esta pantalla ya trabaja aquí.
 */
const PROMESAS = [
  { icono: 'bi-mortarboard', titulo: 'Formación con certificado' },
  { icono: 'bi-file-earmark-text', titulo: 'Solicitudes en línea' },
  { icono: 'bi-people', titulo: 'Tu equipo, al día' },
];

/**
 * El titular, y cambia.
 *
 * Tres frases que dicen lo mismo desde tres sitios —el portal, las solicitudes,
 * la formación—. Rota cada seis segundos y no hay nada que esperar: el campo de
 * correo tiene el foco desde el primer fotograma.
 *
 * Lo que lo hace barato:
 *
 * - **Solo este componente cambia de estado.** El formulario, que es lo que
 *   importa, no se vuelve a pintar nunca por culpa del titular.
 * - **Con la pestaña oculta no rota**: el intervalo sigue vivo pero no escribe
 *   estado, así que no hay render que nadie vaya a ver.
 * - **Con movimiento reducido no rota en absoluto.** Un texto que cambia solo
 *   también es movimiento — y no se registra el intervalo.
 * - **El lector de pantalla oye una frase, una vez.** La que cambia es
 *   `aria-hidden`; la que se lee es la primera, fija, en `sr-only`. Un titular
 *   que se anuncia cada seis segundos encima del formulario lo vuelve
 *   inutilizable con lector.
 *
 * La segunda línea va en `text-accent-on-brand`, que es el naranja SIN
 * oscurecer: 7,04 sobre el navy. `--orange-text` daría 2,52 — lo vigila
 * `test_el_naranja_de_texto_no_se_usa_sobre_superficie_oscura`.
 */
const TITULARES: [string, string][] = [
  ['Todo lo que necesitas,', 'en un solo lugar.'],
  ['Gestiona tus solicitudes', 'sin complicaciones.'],
  ['Aprende. Participa.', 'Crece.'],
];
const CADA_CUANTO_ROTA = 6000;

function Titular() {
  const [i, setI] = useState(0);

  useEffect(() => {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const id = window.setInterval(() => {
      if (document.visibilityState === 'visible') setI((n) => (n + 1) % TITULARES.length);
    }, CADA_CUANTO_ROTA);
    return () => window.clearInterval(id);
  }, []);

  const [linea1, linea2] = TITULARES[i]!;
  return (
    // `min-h` de dos líneas: si el titular siguiente fuera más corto, todo lo
    // que hay debajo subiría un renglón cada seis segundos.
    <h2 className="min-h-[2.5em] text-8 font-bold leading-tight tracking-tight xl:text-9">
      <span className="sr-only">{TITULARES[0]!.join(' ')}</span>
      {/* `key` es lo que vuelve a lanzar la animación: cada frase es un nodo
          nuevo, y un nodo nuevo entra con su `anim-sube-corto`. */}
      <span key={i} aria-hidden="true" className="anim-sube-corto block" style={{ animationDuration: '380ms' }}>
        {linea1}
        <br />
        <span className="text-accent-on-brand">{linea2}</span>
      </span>
    </h2>
  );
}

function Presentacion({ marca }: { marca?: DatosDeMarca }) {
  return (
    // `max-lg:hidden`, no `hidden lg:flex`: el `.hidden` del portal lleva
    // `!important`, así que gana a cualquier variante y el panel no se vería en
    // NINGÚN ancho. Lo cazó `test_hidden_con_variante_no_vuelve_a_la_capa_react`
    // antes de que llegara a la pantalla.
    //
    // Sin `bg-brand` propio desde el 2026-10-01: el panel se apoya en el
    // degradado de `.login-screen`, y el formulario es la única superficie que
    // destaca. Un bloque navy sobre un fondo navy dibujaba una caja que no
    // separaba nada.
    <div className="relative isolate flex flex-col justify-between gap-12 overflow-hidden px-12 py-10 text-on-brand max-lg:hidden">
      <RedDeFondo />

      <div className="anim-sube-corto flex items-center gap-3">
        <Logo marca={marca} />
        <div className="min-w-0">
          <p className="truncate text-5 font-bold">{marca?.portal_name ?? 'Portal de Bienestar'}</p>
          <p className="truncate text-2 text-white/75">
            {marca?.company_name ?? 'Grupo Empresarial Su Servicio'}
          </p>
        </div>
      </div>

      <div className="anim-sube-corto max-w-[30rem]" style={retardo(80)}>
        <Titular />
        {/* El lema es configurable desde Personalización, así que se queda
            aunque el titular cambie: es la voz de la empresa, no la del portal. */}
        {marca?.login_subtitle && (
          <p className="mt-4 text-5 text-white/85">{marca.login_subtitle}</p>
        )}
      </div>

      <div className="anim-sube-corto" style={retardo(160)}>
        <ul className="flex flex-wrap gap-2">
          {PROMESAS.map((p) => (
            <li
              key={p.titulo}
              className="inline-flex items-center gap-2 rounded-full border border-white/20 px-3 py-1.5 text-2 font-semibold text-white/90"
            >
              <i className={`bi ${p.icono}`} aria-hidden="true" />
              {p.titulo}
            </li>
          ))}
        </ul>
        <p className="mt-4 flex max-w-[34rem] items-start gap-2 text-2 text-white/70">
          <i className="bi bi-shield-lock mt-px shrink-0" aria-hidden="true" />
          <span>
            Uso interno y auditable: cada acción queda registrada. Tus datos personales
            no salen de aquí.
          </span>
        </p>
      </div>
    </div>
  );
}

/**
 * La red del fondo: puntos y líneas, «conexión» sin decirlo.
 *
 * Es un SVG en línea de 12 nodos, no un canvas ni partículas: cero peticiones,
 * cero JavaScript y nada que se mueva después de entrar. Aparece una vez —la
 * misma subida del resto del panel— y se queda quieto: una animación infinita
 * en el fondo de la pantalla que más tiempo se mira mientras se escribe la
 * contraseña sería trabajo del navegador para nada.
 *
 * Opacidad baja y en blanco, así que hereda el color de marca de cualquier
 * instalación sin saber cuál es.
 */
const NODOS: [number, number][] = [
  [40, 70], [150, 30], [250, 110], [370, 60], [90, 190], [210, 230],
  [330, 200], [440, 150], [140, 330], [280, 340], [400, 300], [500, 260],
];
/** Las líneas ya resueltas a coordenadas: así el render no indexa nada. */
const ENLACES = ([
  [0, 1], [1, 2], [2, 3], [0, 4], [4, 5], [2, 5], [5, 6], [3, 7], [6, 7],
  [4, 8], [5, 9], [8, 9], [9, 10], [6, 10], [10, 11], [7, 11],
] as const).map(([a, b]) => ({ clave: `${a}-${b}`, desde: NODOS[a]!, hasta: NODOS[b]! }));

function RedDeFondo() {
  return (
    <svg
      // La opacidad va en los trazos y NO en el `<svg>`: `anim-sube` termina en
      // `opacity: 1` y se queda ahí (`both`), y una animación gana a cualquier
      // clase. Con `opacity-[.16]` aquí, la red salía a plena intensidad.
      className="anim-sube pointer-events-none absolute -right-20 top-[18%] -z-10 h-auto w-[520px]"
      style={retardo(200)}
      viewBox="0 0 540 380"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <g stroke="currentColor" strokeOpacity=".13" strokeWidth="1">
        {ENLACES.map(({ clave, desde, hasta }) => (
          <line key={clave} x1={desde[0]} y1={desde[1]} x2={hasta[0]} y2={hasta[1]} />
        ))}
      </g>
      <g fill="currentColor" fillOpacity=".22">
        {NODOS.map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r={i % 3 === 0 ? 4 : 2.5} />
        ))}
      </g>
    </svg>
  );
}

/**
 * La placa del logo.
 *
 * El fondo es `#fff` literal y no un token, **a propósito**: `--surface` se
 * invierte en tema oscuro, y esto es la placa sobre la que se apoya el logo de
 * la empresa, que viene con tinta oscura. Con el token, en oscuro el logo
 * desaparecería sobre su propio fondo. Estaba escrito así en `applyBranding` y
 * se conserva el motivo.
 */
function Logo({ marca }: { marca?: { logo_url: string | null; portal_name: string } }) {
  if (!marca?.logo_url) {
    return (
      <span
        className="flex size-12 shrink-0 items-center justify-center rounded-sm border border-white/25 text-7 font-black text-on-brand"
        aria-hidden="true"
      >
        B
      </span>
    );
  }
  return (
    <span
      className="flex h-12 w-40 shrink-0 items-center justify-center rounded-sm p-1 shadow-raised"
      style={{ background: '#fff' }}
    >
      <img
        src={marca.logo_url}
        alt={marca.portal_name}
        className="max-h-full max-w-full object-contain"
      />
    </span>
  );
}

/**
 * La superficie del formulario.
 *
 * Es la única caja clara de la pantalla y por eso lleva la sombra más alta del
 * sistema (`shadow-overlay`): sobre el navy tiene que leerse como lo que se
 * toca. Entra con `anim-sube` —15 px, 420 ms— y su contenido detrás, en tres
 * tiempos muy cortos (ver `Entrar`).
 */
function Tarjeta({ marca, conMarca = false, children }: {
  marca?: { logo_url: string | null; portal_name: string };
  /** La cabecera con la marca. Un booleano y no «si llegó `marca`»: la marca
   *  carga después, y la cabecera aparecería de golpe empujando el formulario. */
  conMarca?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="anim-sube rounded-md border border-line bg-surface p-6 shadow-overlay sm:p-8">
      {/* Sin el panel de la izquierda —recuperar y restablecer son de una
          columna— esta es la única marca de la pantalla. */}
      {conMarca && (
        <header className="mb-6 flex items-center gap-3">
          <span className="flex size-11 shrink-0 items-center justify-center rounded-sm bg-brand text-6 font-black text-on-brand">
            {marca?.logo_url
              ? <img src={marca.logo_url} alt="" className="max-h-full max-w-full object-contain p-1" />
              : 'B'}
          </span>
          <p className="min-w-0 truncate text-3 font-semibold text-ink-2">
            {marca?.portal_name ?? 'Portal de Bienestar'}
          </p>
        </header>
      )}
      {children}
    </div>
  );
}

/** Lo que tienen en común los enlaces de texto de esta pantalla. */
const enlace = 'rounded-xs transition-colors duration-150 hover:underline focus-visible:focus-anillo';

/* ── Entrar ────────────────────────────────────────────────────────────── */

function Entrar() {
  const entrar = useEntrar();
  const [correo, setCorreo] = useState('');
  const [clave, setClave] = useState('');
  const [verClave, setVerClave] = useState(false);

  return (
    // Sin `marca`: a la izquierda —o encima, en móvil— ya está. Repetir el logo
    // dentro de la tarjeta era la segunda vez que la pantalla decía de quién es.
    <Tarjeta>
      <header className="anim-sube-corto mb-6" style={retardo(60)}>
        <h1 className="text-7 font-bold tracking-tight text-brand-ink">Bienvenido/a</h1>
        <p className="mt-1 text-3 text-muted">Ingresa con tu cuenta corporativa</p>
      </header>

      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          entrar.mutate({ correo, clave }, {
            onSuccess: () => { void window.APP?.startApp?.(); },
          });
        }}
      >
        <div className="anim-sube-corto flex flex-col gap-4" style={retardo(120)}>
          <Field label="Correo electrónico">
            <Input
              type="email"
              // `autocomplete` de verdad: el formulario llevaba `autocomplete="off"`,
              // que impide al gestor de contraseñas rellenarlo. En una intranet
              // corporativa eso empuja a contraseñas memorizables, es decir peores.
              autoComplete="email"
              className={campo}
              value={correo}
              onChange={(e) => setCorreo(e.target.value)}
              placeholder="tu@empresa.com"
              autoFocus
            />
          </Field>

          <div className="flex flex-col gap-1.5">
            <Field label="Contraseña">
              <span className="relative block">
                <Input
                  type={verClave ? 'text' : 'password'}
                  autoComplete="current-password"
                  className={`${campo} pr-11`}
                  value={clave}
                  onChange={(e) => setClave(e.target.value)}
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  className="absolute inset-y-1 right-1 flex w-9 items-center justify-center rounded-xs text-muted
                             transition-colors duration-150 hover:bg-sunken hover:text-ink focus-visible:focus-anillo"
                  onClick={() => setVerClave((v) => !v)}
                  // Era un botón con un icono y nada más: se leía «botón».
                  aria-label={verClave ? 'Ocultar la contraseña' : 'Mostrar la contraseña'}
                  aria-pressed={verClave}
                >
                  <i className={`bi ${verClave ? 'bi-eye-slash' : 'bi-eye'}`} aria-hidden="true" />
                </button>
              </span>
            </Field>
            {/* Junto al campo al que se refiere, que es donde se busca. */}
            <a
              href="#"
              className={`${enlace} self-end text-2 font-semibold text-brand-ink`}
              onClick={(e) => { e.preventDefault(); window.APP?.showForgot?.(); }}
            >
              ¿Olvidaste tu contraseña?
            </a>
          </div>
        </div>

        {entrar.isError && (
          <p
            className="anim-sube-corto flex items-start gap-2 rounded-sm border border-danger bg-danger/8 p-3 text-3 text-danger-ink"
            role="alert"
          >
            <i className="bi bi-exclamation-circle mt-px shrink-0" aria-hidden="true" />
            <span>{(entrar.error as Error).message}</span>
          </p>
        )}

        <div className="anim-sube-corto" style={retardo(180)}>
          {/* Navy y no naranja, aunque la portada pinte su «Entrar» en naranja:
              dentro del portal el acento no rellena botones (lo vigila
              `test_reparto_del_acento.py`). Lo que sí cambia es la respuesta:
              sube un píxel al apuntarlo, la flecha avanza, y al pulsarlo vuelve
              a su sitio — que es la confirmación de que el clic llegó. */}
          <Button
            type="submit"
            block
            loading={entrar.isPending}
            className="group min-h-11 transition-[background-color,border-color,color,box-shadow,transform]
                       hover:-translate-y-px hover:shadow-raised active:translate-y-0 active:shadow-none
                       disabled:hover:translate-y-0 disabled:hover:shadow-none
                       motion-reduce:hover:translate-y-0"
          >
            {entrar.isPending ? 'Ingresando…' : (
              <>
                Ingresar
                <i
                  className="bi bi-arrow-right ml-2 inline-block transition-transform duration-150
                             group-hover:translate-x-0.5 motion-reduce:transition-none"
                  aria-hidden="true"
                />
              </>
            )}
          </Button>
        </div>

        <div className="anim-sube-corto flex flex-col gap-4" style={retardo(240)}>
          {/* El primer ingreso es donde más gente se atasca, así que la ayuda se
              queda —entera—, pero plegada: abierta ocupaba más que el propio
              formulario y competía con él. `<details>` es nativo: Enter y
              Espacio lo abren y el lector anuncia «contraído/expandido» sin
              una línea de JavaScript.

              Sin correo de soporte porque no hay ninguno configurado —branding
              no trae ese campo— y escribir uno inventado sería peor que no
              ponerlo. */}
          <details className="group/ayuda rounded-sm border border-line text-2 text-ink-2">
            <summary
              className="flex min-h-10 cursor-pointer list-none items-center gap-2 rounded-sm px-3 py-2
                         font-semibold text-ink-2 transition-colors duration-150 hover:bg-sunken hover:text-brand-ink
                         focus-visible:focus-anillo pointer-coarse:min-h-11
                         [&::-webkit-details-marker]:hidden"
            >
              <i className="bi bi-question-circle" aria-hidden="true" />
              <span className="flex-1">¿Problemas para entrar?</span>
              <i
                className="bi bi-chevron-down inline-block text-1 transition-transform duration-150 group-open/ayuda:rotate-180 motion-reduce:transition-none"
                aria-hidden="true"
              />
            </summary>
            <p className="border-t border-line px-3 py-3 leading-relaxed">
              Tu usuario es tu <strong>correo corporativo</strong>. Si es tu primer ingreso, la
              contraseña llegó a ese mismo correo. Si no la encuentras, usa «¿Olvidaste tu
              contraseña?» o escribe al área de Bienestar.
            </p>
          </details>

          <div className="flex flex-col items-center gap-2 border-t border-line pt-4">
            <a href="/" className={`${enlace} text-2 text-muted hover:text-brand-ink`}>
              <i className="bi bi-arrow-left" aria-hidden="true" /> Volver al inicio
            </a>
            <p className="text-center text-1 text-muted">
              <EnlaceLegal clave="terminos">Términos y Condiciones</EnlaceLegal>
              {' · '}
              <EnlaceLegal clave="privacidad">Política de Tratamiento de Datos</EnlaceLegal>
            </p>
          </div>
        </div>
      </form>
    </Tarjeta>
  );
}

/** Los campos de esta pantalla, un punto más altos que los del resto del
 *  portal: son dos y son todo lo que hay que hacer aquí. El foco es el de
 *  `ui/Field` —borde navy y halo de acento—; al pasar por encima el borde se
 *  adelanta un poco, para que se note dónde se puede escribir. */
const campo = 'min-h-11 hover:border-brand-ink/60';

/** Los documentos legales, que los abre el módulo `Legal` del armazón. Eran
 *  `onclick="event.preventDefault();Legal.viewDocument('terminos')"` escrito en
 *  el atributo. */
function EnlaceLegal({ clave, children }: { clave: string; children: React.ReactNode }) {
  return (
    <a
      href="#"
      className="text-muted underline hover:no-underline focus-visible:focus-anillo"
      onClick={(e) => { e.preventDefault(); window.Legal?.viewDocument?.(clave); }}
    >
      {children}
    </a>
  );
}

/* ── Pedir el enlace ───────────────────────────────────────────────────── */

function Olvide() {
  const pedir = usePedirEnlace();
  const [correo, setCorreo] = useState('');

  return (
    <>
      <h1 className="mb-1 text-6 font-bold tracking-tight text-brand-ink">Recuperar contraseña</h1>
      <p className="mb-5 text-3 text-muted">Te enviaremos un enlace a tu correo</p>
      <form
        className="flex flex-col gap-3"
        onSubmit={(e) => { e.preventDefault(); pedir.mutate(correo); }}
      >
        <Field label="Correo electrónico">
          <Input
            type="email"
            autoComplete="email"
            value={correo}
            onChange={(e) => setCorreo(e.target.value)}
            placeholder="tu@empresa.com"
          />
        </Field>

        {/* El mensaje NO dice si el correo existe, y eso es deliberado: decirlo
            convierte esta pantalla en una forma de averiguar quién trabaja aquí.
            Por eso el éxito se enseña igual pase lo que pase en el servidor. */}
        {pedir.isSuccess && (
          <p className="rounded-sm border border-ok bg-ok/10 p-3 text-3 text-ok-ink" role="status">
            Si el correo existe, recibirás instrucciones en tu bandeja.
          </p>
        )}
        {pedir.isError && (
          <p className="rounded-sm border border-danger bg-danger/8 p-3 text-3 text-danger-ink" role="alert">
            No se pudo enviar. Inténtalo de nuevo.
          </p>
        )}

        <Button type="submit" block loading={pedir.isPending} disabled={!correo.trim()}
          razonDeshabilitado="Escribe tu correo.">
          Enviar enlace
        </Button>
        <p className="text-center text-2">
          <a
            href="#"
            className="text-brand-ink hover:underline focus-visible:focus-anillo"
            onClick={(e) => { e.preventDefault(); window.APP?.showLogin?.(); }}
          >
            ← Volver a entrar
          </a>
        </p>
      </form>
    </>
  );
}

/* ── Fijar la contraseña nueva ─────────────────────────────────────────── */

/** El mismo mínimo que exige el servidor desde el 2026-09-28. Aquí es la
 *  respuesta rápida —para no mandar una petición que se sabe que falla—, no la
 *  guarda: la guarda es `exigir_clave_valida()`. */
const LARGO_MINIMO = 8;

function Restablecer() {
  const token = tokenDeRestablecer();
  const guardar = useRestablecerClave();
  const [clave, setClave] = useState('');
  const [repetida, setRepetida] = useState('');
  const [listo, setListo] = useState(false);

  const corta = clave.length > 0 && clave.length < LARGO_MINIMO;
  const noCoinciden = repetida.length > 0 && clave !== repetida;
  const puede = clave.length >= LARGO_MINIMO && clave === repetida;

  if (listo) {
    return (
      <p className="rounded-sm border border-ok bg-ok/10 p-4 text-center text-3 text-ok-ink" role="status">
        <i className="bi bi-check-circle text-6" aria-hidden="true" />
        <strong className="mt-2 block">Contraseña actualizada</strong>
        Ya puedes entrar con la nueva.
        <span className="mt-3 block">
          <Button onClick={() => { window.location.hash = ''; window.APP?.showLogin?.(); }}>
            Ir a entrar
          </Button>
        </span>
      </p>
    );
  }

  if (!token) {
    return (
      <p className="rounded-sm border border-danger bg-danger/8 p-4 text-3 text-danger-ink">
        <strong className="block">Este enlace está incompleto</strong>
        Ábrelo tal como te llegó al correo, sin recortarlo. Si no lo encuentras, pide uno
        nuevo desde «¿Olvidaste tu contraseña?».
      </p>
    );
  }

  return (
    <>
      <h1 className="mb-1 text-6 font-bold tracking-tight text-brand-ink">Nueva contraseña</h1>
      <p className="mb-5 text-3 text-muted">Define tu nueva contraseña</p>
      <form
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          guardar.mutate({ token, clave }, { onSuccess: () => setListo(true) });
        }}
      >
        <Field
          label="Nueva contraseña"
          ayuda={`Mínimo ${LARGO_MINIMO} caracteres.`}
          error={corta ? `Te faltan ${LARGO_MINIMO - clave.length} caracteres.` : undefined}
        >
          <Input
            type="password"
            autoComplete="new-password"
            value={clave}
            onChange={(e) => setClave(e.target.value)}
          />
        </Field>
        <Field
          label="Confirmar contraseña"
          error={noCoinciden ? 'Las dos no coinciden.' : undefined}
        >
          <Input
            type="password"
            autoComplete="new-password"
            value={repetida}
            onChange={(e) => setRepetida(e.target.value)}
          />
        </Field>

        {guardar.isError && (
          <p className="rounded-sm border border-danger bg-danger/8 p-3 text-3 text-danger-ink" role="alert">
            {(guardar.error as Error).message}
          </p>
        )}

        <Button
          type="submit"
          block
          loading={guardar.isPending}
          disabled={!puede}
          razonDeshabilitado={
            clave.length < LARGO_MINIMO
              ? `La contraseña necesita ${LARGO_MINIMO} caracteres.`
              : 'Las dos contraseñas tienen que coincidir.'
          }
        >
          Guardar contraseña
        </Button>
        <p className="text-center text-2">
          <a
            href="#"
            className="text-brand-ink hover:underline focus-visible:focus-anillo"
            onClick={(e) => {
              e.preventDefault();
              window.location.hash = '';
              window.APP?.showLogin?.();
            }}
          >
            ← Volver a entrar
          </a>
        </p>
      </form>
    </>
  );
}
